import {createReducer, on} from '@ngrx/store';
import {
  ClearState,
  FetchCoachesFilterFailure,
  FetchCoachesFilterRequest,
  FetchCoachesFilterSuccess,
  FetchParticipantAttendanceFailure,
  FetchParticipantAttendanceRequest,
  FetchParticipantAttendanceSuccess,
  FetchSessionDeliveryFilterFailure,
  FetchSessionDeliveryFilterRequest,
  FetchSessionDeliveryFilterSuccess,
  FetchSessionDetailsFailure,
  FetchSessionDetailsRequest,
  FetchSessionDetailsSuccess,
  FetchSessionListFailure,
  FetchSessionListRequest,
  FetchSessionListSuccess,
  FetchSessionResourcesFailure,
  FetchSessionResourcesRequest,
  FetchSessionResourcesSuccess,
  MarkAttendanceFailure,
  MarkAttendanceRequest,
  MarkAttendanceSuccess,
  MarkParticipantDropoutFailure,
  MarkParticipantDropoutRequest,
  MarkParticipantDropoutSuccess,
  UpdateSessionDetailsFailure,
  UpdateSessionDetailsRequest,
  UpdateSessionDetailsSuccess
} from "./sessions-list.actions";
import {Filter} from "../../../../models/filter";
import {
  FetchSessionResourcesModel
} from "../models/fetch-session-resources.model";
import {FetchSessionDetailsModel} from "../models/fetch-session-details.model";
import {FetchSessionListModel} from "../models/fetch-session-list.model";
import {
  FetchParticipantAttendanceModel
} from "../models/fetch-participant-attendance.model";

// State for this feature (User)
export interface SessionsListState {
  loading: boolean,
  sessionList: {
    list: FetchSessionListModel['response']['data'],
    loading: boolean
  },
  sessionDeliveryFilter: Filter;
  sessionResources: FetchSessionResourcesModel['data'];
  sessionDetails: {
    loading: boolean,
    details: FetchSessionDetailsModel['data']
  };
  coachesFilter: Filter,
  participantAttendanceInfo: {
    data: FetchParticipantAttendanceModel['response']['data'],
    loading: boolean
  },
  markAttendance: {
    loading: boolean
  }
}

const initialState: SessionsListState = {
  loading: false,
  sessionDeliveryFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  sessionResources: null,
  sessionDetails: {
    loading: false,
    details: null
  },
  coachesFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false,
  },
  sessionList: {
    list: [],
    loading: false
  },
  participantAttendanceInfo: {
    data: {
      name: '',
      description: '',
      startDate: '',
      format: null,
      location: null,
      duration: null,
      isComplete: null,
      note: null,
      groupCoaches: [],
      sessionAttendance: null
    },
    loading: false
  },
  markAttendance: {
    loading: false
  }
};

export const reducer = createReducer(
  initialState,

  // CLEAR STATE
  on(ClearState, () => initialState),

  // FETCH SESSION DELIVERY FILTER
  on(FetchSessionDeliveryFilterRequest, (state) => ({
    ...state,
    sessionDeliveryFilter: {
      ...initialState.sessionDeliveryFilter,
      loading: true
    }
  })),
  on(FetchSessionDeliveryFilterSuccess, (state, props) => ({
    ...state,
    sessionDeliveryFilter: {
      ...props.sessionDeliveryFilter,
      loading: false
    }
  })),
  on(FetchSessionDeliveryFilterFailure, (state) => ({
    ...state,
    sessionDeliveryFilter: {
      ...initialState.sessionDeliveryFilter,
      loading: false
    }
  })),

  // FETCH SESSION RESOURCES
  on(FetchSessionResourcesRequest, (state, props) => ({
    ...state,
    sessionResources: {
      ...initialState.sessionResources,
      list: [
        ...(
          props.payload?.pageNumber ?
          props.payload?.pageNumber === 1 ? [] : [...state.sessionResources.list] :
            []
        )
      ],
      loading: true
    }
  })),
  on(FetchSessionResourcesSuccess, (state, props) => ({
    ...state,
    sessionResources: {
      ...props.data,
      list: [
        ...(props.data.pageNumber === 1 ? [
          ...props.data.list
        ] : [
          ...state.sessionResources.list,
          ...props.data.list
        ])
      ],
      loading: false
    }
  })),
  on(FetchSessionResourcesFailure, (state) => ({
    ...state,
    sessionResources: {
      ...state.sessionResources,
      loading: false
    }
  })),

  // UPDATE SESSION DETAILS
  on(UpdateSessionDetailsRequest, (state) => ({
    ...state,
    sessionDetails: {
      ...state.sessionDetails,
      loading: true
    }
  })),
  on(UpdateSessionDetailsSuccess, (state, props) => ({
    ...state,
    sessionDetails: {
      details: props.sessionDetails,
      loading: false
    }
  })),
  on(UpdateSessionDetailsFailure, (state) => ({
    ...state,
    sessionDetails: {
      ...state.sessionDetails,
      loading: false
    }
  })),

  // FETCH SESSION DETAILS
  on(FetchSessionDetailsRequest, (state) => ({
    ...state,
    sessionDetails: {
      ...initialState.sessionDetails,
      loading: true
    }
  })),
  on(FetchSessionDetailsSuccess, (state, props) => ({
    ...state,
    sessionDetails: {
      details: props.sessionDetails,
      loading: false
    }
  })),
  on(FetchSessionDetailsFailure, (state) => ({
    ...state,
    sessionDetails: {
      ...initialState.sessionDetails,
      loading: false
    }
  })),

  // FETCH COACHES FILTER LIST
  on(FetchCoachesFilterRequest, (state) => ({
    ...state,
    coachesFilter: {
      ...initialState.coachesFilter,
      loading: true
    }
  })),
  on(FetchCoachesFilterSuccess, (state, props) => ({
    ...state,
    coachesFilter: {
      ...props.coachesFilter,
      loading: false
    }
  })),
  on(FetchCoachesFilterFailure, (state) => ({
    ...state,
    coachesFilter: {
      ...state.coachesFilter,
      loading: false
    }
  })),

  // FETCH SESSION LIST
  on(FetchSessionListRequest, (state) => ({
    ...state,
    sessionList: {
      ...initialState.sessionList,
      loading: true
    }
  })),
  on(FetchSessionListSuccess, (state, props) => ({
    ...state,
    sessionList: {
      list: props.sessionList,
      loading: false,
    }
  })),
  on(FetchSessionListFailure, (state) => ({
    ...state,
    sessionList: {
      ...state.sessionList,
      loading: false
    }
  })),

  // FETCH PARTICIPANT ATTENDANCE
  on(FetchParticipantAttendanceRequest, (state) => ({
    ...state,
    participantAttendanceInfo: {
      ...initialState.participantAttendanceInfo,
      loading: true
    }
  })),
  on(FetchParticipantAttendanceSuccess, (state, props) => ({
    ...state,
    participantAttendanceInfo: {
      data: props.participantAttendanceInfo,
      loading: false,
    }
  })),
  on(FetchParticipantAttendanceFailure, (state) => ({
    ...state,
    participantAttendanceInfo: {
      ...state.participantAttendanceInfo,
      loading: false
    }
  })),

  // MARK ATTENDANCE
  on(MarkAttendanceRequest, (state) => ({
    ...state,
    markAttendance: {
      loading: true
    }
  })),
  on(MarkAttendanceSuccess, (state, props) => ({
    ...state,
    sessionDetails: {
      details: props.data,
      loading: false
    },
    markAttendance: {
      loading: false
    }
  })),
  on(MarkAttendanceFailure, (state) => ({
    ...state,
    markAttendance: {
      loading: false
    }
  })),
  
  //mark dropout
  on(MarkParticipantDropoutRequest, (state) => state),
  on(MarkParticipantDropoutSuccess, (state) => ({
    ...state,
    sessionDetails: {
      ...initialState.sessionDetails,
      loading: true
    }
  })),
  on(MarkParticipantDropoutFailure, (state) => state),
);
